// components/project/economic/BillingWorkflowModals.jsx
// FASE BILLING 1.4 — Modali OBBLIGATORIE del workflow economico unificato.
// Nessun cambio stato diretto: fattura, incasso e storno passano SEMPRE da qui.
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Badge } from '../../ui/badge';
import { Receipt, CreditCard, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../../../lib/utils';
import { toast } from 'sonner';

export const PAYMENT_METHODS = [
  { value: 'bonifico', label: 'Bonifico' },
  { value: 'carta_pos', label: 'Carta/POS' },
  { value: 'assegno', label: 'Assegno' },
  { value: 'contanti', label: 'Contanti' },
  { value: 'altro', label: 'Altro' },
];
const methodLabel = (v) => (PAYMENT_METHODS.find(m => m.value === v)?.label || v || '—');
const todayISO = () => new Date().toISOString().slice(0, 10);
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random());

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || 'Operazione non riuscita');
  return data;
}

// ---------- REGISTRA FATTURA EMESSA ----------
export function InvoiceModal({ open, onClose, projectId, tranche, onDone }) {
  const [form, setForm] = useState({ invoice_number: '', invoice_date: todayISO(), due_date: '', invoice_amount: '', notes: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && tranche) {
      setForm({ invoice_number: '', invoice_date: todayISO(), due_date: '',
        invoice_amount: String(tranche.amount ?? ''), notes: '' });
    }
  }, [open, tranche]);
  if (!tranche) return null;
  const maxAmount = Number(tranche.amount || 0);
  const amt = parseFloat(form.invoice_amount || '0');
  const valid = form.invoice_number.trim() && form.invoice_date && form.due_date && amt > 0 && Math.abs(amt - maxAmount) <= 0.01;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await postJSON(`/api/projects/${projectId}/billing/invoice`, {
        source_type: tranche.source_type, source_id: tranche.source_id, tranche_key: tranche.tranche_key,
        invoice_number: form.invoice_number.trim(), invoice_date: form.invoice_date,
        due_date: form.due_date, invoice_amount: amt, notes: form.notes || null,
      });
      toast.success('Fattura registrata');
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-md" data-testid="invoice-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-blue-600" /> Registra fattura emessa</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {(tranche.client_name || tranche.project_number) && (
            <div className="text-xs font-semibold text-[#171717]" data-testid="invoice-modal-context">
              {tranche.client_name || ''}{tranche.project_number ? ` · ${tranche.project_number}` : ''}
            </div>
          )}
          <div className="text-xs text-[#737373]">{tranche.source_label} · <b>{tranche.source_ref || tranche.code}</b> · residuo fatturabile {formatCurrency(maxAmount)}</div>
          <div>
            <Label className="text-xs">Numero fattura *</Label>
            <Input data-testid="invoice-number-input" value={form.invoice_number}
              onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} placeholder="es. 123/2026" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Data fattura *</Label>
              <Input type="date" data-testid="invoice-date-input" value={form.invoice_date}
                onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Data scadenza *</Label>
              <Input type="date" data-testid="invoice-due-input" value={form.due_date}
                onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Importo fatturato *</Label>
            <Input type="number" step="0.01" readOnly data-testid="invoice-amount-input"
              value={form.invoice_amount} className="bg-[#F8FAFC]" />
            <p className="text-[11px] text-[#737373] mt-1">Importo pari alla tranche ({formatCurrency(maxAmount)}). La fatturazione parziale non è supportata.</p>
          </div>
          <div>
            <Label className="text-xs">Note (facoltative)</Label>
            <Textarea rows={2} data-testid="invoice-notes-input" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="invoice-cancel-btn">Annulla</Button>
          <Button onClick={submit} disabled={!valid || saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="invoice-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Receipt className="w-4 h-4 mr-1" />}
            Registra fattura {amt > 0 ? formatCurrency(amt) : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- REGISTRA INCASSO ----------
export function PaymentModal({ open, onClose, projectId, tranche, onDone }) {
  const [form, setForm] = useState({ payment_date: todayISO(), amount: '', payment_method: '', payment_reference: '', notes: '' });
  const [idem, setIdem] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && tranche) {
      setForm({ payment_date: todayISO(), amount: String(tranche.outstanding ?? ''), payment_method: '', payment_reference: '', notes: '' });
      setIdem(uid());
    }
  }, [open, tranche]);
  if (!tranche) return null;
  const outstanding = Number(tranche.outstanding || 0);
  const amt = parseFloat(form.amount || '0');
  const valid = form.payment_date && amt > 0 && amt <= outstanding + 0.01 && form.payment_method;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await postJSON(`/api/projects/${projectId}/billing/payment`, {
        source_type: tranche.source_type, source_id: tranche.source_id, tranche_key: tranche.tranche_key,
        amount: amt, payment_date: form.payment_date, payment_method: form.payment_method,
        payment_reference: form.payment_reference || null, notes: form.notes || null, idempotency_key: idem,
      });
      toast.success(amt >= outstanding - 0.005 ? 'Incasso totale registrato' : 'Incasso parziale registrato');
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-md" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-600" /> Registra incasso</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {(tranche.client_name || tranche.project_number) && (
            <div className="text-xs font-semibold text-[#171717]" data-testid="payment-modal-context">
              {tranche.client_name || ''}{tranche.project_number ? ` · ${tranche.project_number}` : ''}
            </div>
          )}
          <div className="rounded-lg bg-[#F8FAFC] border border-[#E5E7EB] p-2.5 text-xs flex items-center justify-between">
            <span className="text-[#737373]">{tranche.source_label} · <b>{tranche.source_ref || tranche.code}</b>{tranche.invoice_number ? ` · Fatt. ${tranche.invoice_number}` : ''}</span>
            <span className="font-semibold text-[#171717]">Residuo {formatCurrency(outstanding)}</span>
          </div>
          {tranche.is_overdue && tranche.days_overdue > 0 && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-2.5 py-2 text-xs text-red-700 flex items-center gap-1.5" data-testid="payment-overdue-notice">
              <AlertTriangle className="w-3.5 h-3.5" /> Fattura scaduta da {tranche.days_overdue} giorni
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Data incasso *</Label>
              <Input type="date" data-testid="payment-date-input" value={form.payment_date}
                onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Importo *</Label>
              <Input type="number" step="0.01" min="0" max={outstanding} data-testid="payment-amount-input"
                value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
          </div>
          {amt > outstanding + 0.01 && <p className="text-[11px] text-red-600">Supera il residuo da incassare ({formatCurrency(outstanding)})</p>}
          <div>
            <Label className="text-xs">Metodo di pagamento *</Label>
            <Select value={form.payment_method} onValueChange={v => setForm(f => ({ ...f, payment_method: v }))}>
              <SelectTrigger data-testid="payment-method-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value} data-testid={`payment-method-${m.value}`}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Riferimento pagamento (CRO/TRN, facoltativo)</Label>
            <Input data-testid="payment-reference-input" value={form.payment_reference}
              onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Note (facoltative)</Label>
            <Textarea rows={2} data-testid="payment-notes-input" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="payment-cancel-btn">Annulla</Button>
          <Button onClick={submit} disabled={!valid || saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payment-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
            Registra incasso {amt > 0 ? formatCurrency(amt) : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- MOVIMENTI + STORNO ----------
export function MovementsModal({ open, onClose, projectId, tranche, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reversing, setReversing] = useState(null); // payment id in reversal form
  const [reason, setReason] = useState('');
  const [revDate, setRevDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!tranche) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ source_type: tranche.source_type, source_id: tranche.source_id, tranche_key: tranche.tranche_key });
      const res = await fetch(`/api/projects/${projectId}/billing/tranche-movements?${q}`, { credentials: 'include' });
      setData(await res.json());
    } catch (e) { toast.error('Errore nel caricamento movimenti'); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open && tranche) { setReversing(null); setReason(''); setRevDate(todayISO()); load(); } }, [open, tranche]);
  if (!tranche) return null;

  const doReverse = async (paymentId) => {
    if (!reason.trim() || !revDate || saving) return;
    setSaving(true);
    try {
      await postJSON(`/api/billing/payment/${paymentId}/reverse`, { reason: reason.trim(), reversal_date: revDate, notes: null });
      toast.success('Incasso stornato');
      setReversing(null); setReason('');
      await load();
      onDone && onDone();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const movements = data?.movements || [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-lg" data-testid="movements-modal">
        <DialogHeader>
          <DialogTitle>Movimenti incasso — {tranche.source_ref || tranche.code}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-1 max-h-[55vh] overflow-y-auto">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#737373]">Incassato netto</span>
            <span className="font-bold text-[#171717]" data-testid="movements-paid-total">{formatCurrency(data?.paid_total || 0)}</span>
          </div>
          {loading ? <p className="text-sm text-[#a3a3a3] py-4 text-center">Caricamento...</p> :
            movements.length === 0 ? <p className="text-sm text-[#a3a3a3] py-4 text-center">Nessun movimento registrato.</p> :
              movements.map((m) => (
                <div key={m.id} className="rounded-lg border border-[#EDEDED] p-2.5" data-testid={`movement-row-${m.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {m.movement_type === 'reversal'
                        ? <Badge className="bg-red-100 text-red-700 border border-red-200">Storno</Badge>
                        : m.is_legacy
                          ? <Badge className="bg-gray-100 text-gray-600 border border-gray-200">Storico</Badge>
                          : <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">Incasso</Badge>}
                      <span className="text-sm font-semibold">{formatCurrency(m.amount)}</span>
                      {m.payment_method && <span className="text-xs text-[#737373]">· {methodLabel(m.payment_method)}</span>}
                    </div>
                    <span className="text-xs text-[#a3a3a3]">{m.payment_date ? formatDate(m.payment_date) : '—'}</span>
                  </div>
                  {m.payment_reference && <p className="text-[11px] text-[#737373] mt-0.5">Rif: {m.payment_reference}</p>}
                  {m.reversal_reason && <p className="text-[11px] text-red-600 mt-0.5">Motivo: {m.reversal_reason}</p>}
                  {m.status === 'reversed' && <p className="text-[11px] text-red-500 mt-0.5">Stornato</p>}
                  {m.movement_type === 'payment' && m.status === 'valid' && !m.is_legacy && (
                    reversing === m.id ? (
                      <div className="mt-2 space-y-2 border-t border-[#F0F0F0] pt-2">
                        <Input placeholder="Motivo storno *" value={reason} onChange={e => setReason(e.target.value)} data-testid="reversal-reason-input" />
                        <Input type="date" value={revDate} onChange={e => setRevDate(e.target.value)} data-testid="reversal-date-input" />
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setReversing(null)} disabled={saving}>Annulla</Button>
                          <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" disabled={!reason.trim() || saving}
                            onClick={() => doReverse(m.id)} data-testid="reversal-submit-btn">
                            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />} Conferma storno
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button size="sm" variant="ghost" className="mt-1 h-7 text-xs text-red-600 hover:bg-red-50"
                        onClick={() => { setReversing(m.id); setReason(''); setRevDate(todayISO()); }} data-testid={`reverse-btn-${m.id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Storna incasso
                      </Button>
                    )
                  )}
                </div>
              ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="movements-close-btn">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OverdueBadge({ days }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200" data-testid="overdue-badge">
      <AlertTriangle className="w-3 h-3" /> Scaduta {days > 0 ? `${days}gg` : ''}
    </span>
  );
}
